import { api } from "@/api/client";
import { saveBlob } from "@/lib/download";

// Пустой GUID 1С = «характеристика не задана» (база ПГП).
export const ZERO_GUID = "00000000-0000-0000-0000-000000000000";
// Канал «все МП» приходит пустой строкой.
export const MP_CHANNEL = "";

export interface ComponentRef {
  nomenclature_key: string;
  characteristic_key: string;
  name: string | null;
}

export interface OptShortfall {
  component: ComponentRef;
  reserved: number;
  backed: number;
  shortfall: number;
}

export interface ChannelAvailabilityItem {
  nomenclature_key: string;
  characteristic_key: string;
  name: string | null;
  char_name: string | null; // Description характеристики (напр. «ОПТ»); пусто у ПГП-базы
  total: number; // сырой чистый пул (до претензий)
  per_channel: Record<string, number>; // {канал: свободный eligible-сток}
}

export interface DeficitAvailItem {
  nomenclature_key: string;
  characteristic_key: string;
  name: string | null;
  char_name: string | null;
  free: number;
}

export interface ChannelAvailabilityReport {
  snapshot_at: string | null;
  as_of: string;
  thresholds: Record<string, number>; // {канал: порог_ОСГ_дни}
  items: ChannelAvailabilityItem[];
  deficit_items: DeficitAvailItem[];
  opt_reserve_shortfalls: OptShortfall[];
}

export interface BatchDetail {
  batch_id: string;
  expiration_date: string | null;
  remaining_osg_days: number | null;
  qty: number;
  eligible: Record<string, boolean>;
}

export interface ComponentBatchesReport {
  nomenclature_key: string;
  characteristic_key: string;
  name: string | null;
  snapshot_at: string | null;
  as_of: string;
  thresholds: Record<string, number>;
  batches: BatchDetail[];
}

export interface SnapshotSummary {
  id: string;
  taken_at: string;
  rows_count: number;
  note: string | null;
}

export async function getChannelAvailability(): Promise<ChannelAvailabilityReport> {
  const { data } = await api.get<ChannelAvailabilityReport>("/supply/availability/by-channel");
  return data;
}

export async function refreshSnapshot(): Promise<SnapshotSummary> {
  const { data } = await api.post<SnapshotSummary>("/supply/snapshot/refresh");
  return data;
}

export async function getComponentBatches(
  nom: string,
  char: string,
): Promise<ComponentBatchesReport> {
  const { data } = await api.get<ComponentBatchesReport>("/supply/availability/component-batches", {
    params: { nom, char },
  });
  return data;
}

// ── Этап 2: конструктор плана + проверка + commit ──────────────────────────
export interface PositionView {
  nomenclature_key: string;
  characteristic_key: string;
  name: string | null;
  char_name: string | null;
  channel: string; // выведен из характеристики (пусто = все МП)
  mp_list: string[];
}

export interface PlanLineIn {
  nomenclature_key: string;
  characteristic_key: string;
  qty: number;
}

export interface LedgerEntry {
  component: ComponentRef;
  kind: string; // base | deficit
  channel: string | null;
  required: number;
  available: number; // свободный пул канала ДО плана
  balance: number;
  deficit: boolean;
  backed: number; // сколько план реально подкрепил
  shortfall: number;
}

export interface Deficit {
  component: ComponentRef;
  channel: string | null;
  shortfall: number;
}

export interface Headroom {
  component: ComponentRef;
  channel: string | null;
  remaining: number;
}

export interface ConfigATB {
  position: ComponentRef;
  atb: number;
  summable: boolean;
}

export interface BatchAlloc {
  batch_id: string;
  qty: number;
}

export interface AdvisoryAllocation {
  component: ComponentRef;
  channel: string;
  batches: BatchAlloc[];
}

export interface PlanCheckResult {
  feasible: boolean;
  ledger: LedgerEntry[];
  deficits: Deficit[];
  headroom: Headroom[];
  per_config_atb: ConfigATB[];
  opt_reserve_shortfalls: OptShortfall[];
  advisory: AdvisoryAllocation[];
}

export interface CommitResult {
  committed: boolean;
  commitment_id: string | null;
  deficits: Deficit[];
  opt_reserve_shortfalls: OptShortfall[];
}

export async function getPositions(): Promise<PositionView[]> {
  const { data } = await api.get<PositionView[]>("/supply/positions");
  return data;
}

export async function checkPlan(lines: PlanLineIn[]): Promise<PlanCheckResult> {
  const { data } = await api.post<PlanCheckResult>("/supply/plan/check", { lines });
  return data;
}

export async function commitPlan(manager: string, lines: PlanLineIn[]): Promise<CommitResult> {
  const { data } = await api.post<CommitResult>("/supply/commitments", { manager, lines });
  return data;
}

// ── Этап 3: рационирование дефицита (предложение «что урезать») ─────────────
export type RationStrategy = "strict_priority" | "proportional";

export interface RationLineIn extends PlanLineIn {
  priority?: number; // ранг: меньше = важнее; равные — порядком подачи
  source?: string | null;
}

export interface Grant {
  position: ComponentRef;
  channel: string;
  source: string | null;
  priority: number;
  requested: number;
  granted: number;
}

export interface Cut {
  position: ComponentRef;
  requested: number;
  granted: number;
  binding_components: ComponentRef[];
}

export interface RationProposal {
  strategy: string;
  feasible: boolean;
  granted: Grant[];
  cuts: Cut[];
  binding_components: ComponentRef[];
}

export async function rationPlan(
  lines: RationLineIn[],
  strategy: RationStrategy,
): Promise<RationProposal> {
  const { data } = await api.post<RationProposal>("/supply/plan/ration", { lines, strategy });
  return data;
}

// ── Этап 4: брони + оптовый резерв (управление) ────────────────────────────
export interface ReservationLineView {
  nomenclature_key: string;
  characteristic_key: string;
  qty: number;
  channel: string;
}

export interface CommitmentView {
  id: string;
  manager: string;
  state: string; // active | confirmed | released | expired
  created_at: string;
  expires_at: string | null;
  plan_lines: unknown[];
  reservation_lines: ReservationLineView[];
}

export interface OptReserveView {
  id: number;
  nomenclature_key: string;
  characteristic_key: string;
  qty: number;
  note: string | null;
  updated_at: string;
}

export async function getCommitments(state?: string): Promise<CommitmentView[]> {
  const { data } = await api.get<CommitmentView[]>("/supply/commitments", {
    params: state ? { state } : undefined,
  });
  return data;
}

export async function releaseCommitment(id: string): Promise<CommitmentView> {
  const { data } = await api.post<CommitmentView>(`/supply/commitments/${id}/release`);
  return data;
}

export async function getOptReserves(): Promise<OptReserveView[]> {
  const { data } = await api.get<OptReserveView[]>("/supply/opt-reserves");
  return data;
}

export async function upsertOptReserve(body: {
  nomenclature_key: string;
  characteristic_key: string;
  qty: number;
  note?: string | null;
}): Promise<OptReserveView> {
  const { data } = await api.put<OptReserveView>("/supply/opt-reserves", body);
  return data;
}

export async function deleteOptReserve(id: number): Promise<void> {
  await api.delete(`/supply/opt-reserves/${id}`);
}

// ── Фаза 4: аналитический датасет WB (потребность по регионам) ──────────────
/** Ozon: платное хранение (кластер × SKU) на дату данных юрлица. Справка менеджеру — в
 *  потребность не входит (`ozon_paid_storage` на бэке). */
export interface PaidStorageCell {
  paid_qty: number; // платных штук
  qty: number; // всего штук в строках с платным
  total_day: number; // ₽ начислено за день
  cost_30d: number; // ₽ за 30 дней = total_day × 30 (при текущем остатке)
  data_date: string; // дата данных (последняя по юрлицу)
}

/** Мета платного хранения Ozon в отчёте: даты по юрлицам и что не попало на строки
 *  (предупреждения про это бэк кладёт и в `warnings`). */
export interface OzonPaidStorageMeta {
  horizon_days: number;
  dates: { legal_name: string; date: string | null; stale: boolean }[];
  unmatched: {
    warehouse: string;
    reason: "no_cluster" | "fresh";
    legal_name: string;
    paid_qty: number;
    qty: number;
    total_day: number;
  }[];
  orphans: { region: string; mp_sku: string; paid_qty: number; total_day: number }[];
}

export interface WbAnalyticsRow {
  region: string;
  mp_sku: string;
  nom_key: string;
  nom_name: string;
  article: string;
  orders_qty: number;
  stock_qty: number;
  supply_qty: number;
  days_avail: number; // справочно: дней с остатком > 95-го перцентиля заказов (в скорость не входит)
  max_orders: number; // справочно: 95-й перцентиль дневных заказов
  selling_speed: number; // шт/день = max(speed_7d, speed_30d)
  speed_7d: number; // заказы за последние 7 календарных дней окна / 7
  speed_30d: number; // заказы за 30 дней / 30
  stock_depth: number;
  all_selling_speed: number;
  all_stock_depth: number;
  stock_need: number;
  supply_result: number;
  in_box: number;
  boxes: number;
  orders_7d_qty?: number; // заказов за последние 7 дней окна (числитель speed_7d)
  disabled?: boolean; // канал/регион выключен в настройках → потребность обнулена
  paid_storage?: PaidStorageCell | null; // только Ozon: платное хранение (справка)
}

/** Источник заказов FBO и остатка FBO WB «Распределения», движка и «Производства»: `legacy` —
 *  сырые таблицы (`wb_orders_api`, `all_mp_stocks`), `mart` — витрина dwh `mart.wb_region_*`.
 *  Настройка общая, меняет её только админ (docs/todo/2026-10-19-distribution-wb-mart.md). */
export type WbSource = "legacy" | "mart";

export interface WbAnalyticsReport {
  as_of: string; // дата остатков (WB legacy — all_mp_stocks, mart — fbo_stock_date)
  orders_from: string;
  wb_source?: WbSource | null; // только WB; Ozon/YM — null
  orders_to?: string | null; // WB: последний день окна заказов
  mart_built_at?: string | null; // WB mart: сборка витрины dwh
  /** WB mart: допуск «данные за вчера» — сегодняшняя сборка dwh не прошла, отчёт по вчерашней
   *  (текст — в `warnings`). legacy / Ozon / YM — false */
  mart_stale?: boolean;
  supply_snapshot_at: string | null;
  clusters_fetched_at?: string | null;
  rows_count: number;
  rows: WbAnalyticsRow[];
  warnings: string[];
  unmatched_okrugs: string[];
  unmatched_warehouses: string[];
  all_regions: string[];
  paid_storage?: OzonPaidStorageMeta | null; // только Ozon
  wb_stock_quality?: WbStockQuality[]; // WB mart: остаток FBO региона и его слабая/неопределённая части
}

/** Остаток FBO региона по витрине (`mart`) и его части оценки, шт. */
export interface WbStockQuality {
  region: string;
  fbo_stock: number;
  weak: number; // слабая оценка склада
  unresolved: number; // склад не определён
}

// Редактируемые отсечки calc_supply: target=ГТЗ, catalog=порог общего покрытия, overstock=потолок.
export interface CalcParams {
  target: number;
  catalog: number;
  overstock: number;
}

/** Настройки расчёта, сохранённые ЗА ПОЛЬЗОВАТЕЛЕМ (иначе на F5 они сбрасывались к дефолтам, и
 *  человек считал поставку не тем горизонтом, который сам себе выставил). Дефолты живут на бэке —
 *  фронт их не дублирует, а ждёт этот ответ перед первым расчётом. */
export interface SupplyParams extends CalcParams {
  floor_days: number;
  packaging_per_day: number;
  saved: boolean; // false = своих настроек нет, показаны дефолты
}

export async function getSupplyParams(): Promise<SupplyParams> {
  const { data } = await api.get<SupplyParams>("/supply/params");
  return data;
}

export async function saveSupplyParams(body: Omit<SupplyParams, "saved">): Promise<SupplyParams> {
  const { data } = await api.put<SupplyParams>("/supply/params", body);
  return data;
}

/** Сброс к дефолтам = удалить свою строку, а не записать сегодняшние числа. */
export async function resetSupplyParams(): Promise<SupplyParams> {
  const { data } = await api.delete<SupplyParams>("/supply/params");
  return data;
}

export async function getWbAnalytics(params?: CalcParams): Promise<WbAnalyticsReport> {
  const { data } = await api.get<WbAnalyticsReport>("/supply/analytics/wb", { params });
  return data;
}

// ── Источник WB «Распределения» и сравнение старый × витрина ─────────────────
export interface WbSourceView {
  wb_source: WbSource;
  updated_at: string;
  updated_by: string | null; // username; null — значение миграции/сида
  /** только ответ PUT на `mart`: витрина в допуске «данные за вчера» — текст пометки */
  mart_stale_warning?: string | null;
}

export async function getWbSource(): Promise<WbSourceView> {
  const { data } = await api.get<WbSourceView>("/supply/settings/wb-source");
  return data;
}

/** Только админ. На `mart` бэк сперва проверяет, что витрина прямо сейчас даёт отчёт; нет — 503
 *  с причиной, настройка не меняется. */
export async function setWbSource(wb_source: WbSource): Promise<WbSourceView> {
  const { data } = await api.put<WbSourceView>("/supply/settings/wb-source", { wb_source });
  return data;
}

export interface WbCompareSide {
  as_of: string;
  orders_from: string;
  orders_to: string | null;
  supply_snapshot_at: string | null;
  mart_built_at: string | null;
  mart_stale: boolean; // сторона mart: допуск «данные за вчера»; legacy — false
  rows_count: number;
  warnings: string[];
}

/** Сравниваемые числа строки (`wb_compare.FIELDS` на бэке). */
export const WB_COMPARE_FIELDS = [
  "orders_7d_qty",
  "orders_qty",
  "selling_speed",
  "stock_qty",
  "supply_qty",
  "stock_need",
  "supply_result",
] as const;
export type WbCompareField = (typeof WB_COMPARE_FIELDS)[number];

/** По каждому полю: `<f>_legacy`, `<f>_mart`, `<f>_delta` (= mart − legacy). */
export type WbCompareNums = {
  [K in WbCompareField as `${K}_${"legacy" | "mart" | "delta"}`]: number;
};

export type WbCompareRegion = WbCompareNums & { region: string };

export type WbCompareRow = WbCompareNums & {
  region: string;
  mp_sku: string;
  nom_key: string;
  nom_name: string;
  article: string;
  only_in: WbSource | null; // строка есть только у этого источника
};

export interface WbCompareReport {
  as_of: string; // общий день окна: fbo_stock_date витрины = as_of старого
  legacy: WbCompareSide;
  mart: WbCompareSide;
  regions: WbCompareRegion[];
  rows: WbCompareRow[]; // топ-`top` по |Δ «Допоставить»|, только с Δ ≠ 0
  rows_compared: number; // строк (регион, nm) в объединении источников
  rows_diff: number; // из них с Δ «Допоставить» ≠ 0
  top: number;
  unmatched_warehouses: string[]; // склады старого источника вне маппинга → «Не известен»
  unmatched_okrugs: string[];
  warnings: string[]; // 1С-мета (имена/короба)
}

export async function getWbCompare(
  params: CalcParams & { top?: number },
): Promise<WbCompareReport> {
  const { data } = await api.get<WbCompareReport>("/supply/analytics/wb/compare", { params });
  return data;
}

// «Готово сейчас» (остаток ГП на складе) + «доупаковать» (ATB флаконов), на wb_sku.
export interface WbCapacityItem {
  nom_key: string;
  ready_now: number;
  can_assemble: number;
  warnings: string[];
  /** "" — доупак доступен; "no_base" — товар вне флаконной модели (норма);
   *  "spec_problem" — спеку не удалось развернуть (дефект данных 1С) */
  assembly_block: string;
  /** «Товар на вывод» в 1С: НЕ производим, но остатки возим и распродаём */
  delisting: boolean;
}

export interface WbSupplyCapacityReport {
  availability_as_of: string | null;
  assemble_computed: boolean;
  items: Record<string, WbCapacityItem>; // wb_sku → числа
  n_ready: number;
  n_assemble: number;
  warnings: string[];
}

export async function getWbSupplyCapacity(): Promise<WbSupplyCapacityReport> {
  const { data } = await api.get<WbSupplyCapacityReport>("/supply/analytics/wb/supply-capacity");
  return data;
}

// ── Ozon FBO (та же форма отчёта, регион = кластер) ─────────────────────────
export async function getOzonAnalytics(params?: CalcParams): Promise<WbAnalyticsReport> {
  const { data } = await api.get<WbAnalyticsReport>("/supply/analytics/ozon", { params });
  return data;
}

export async function getOzonSupplyCapacity(): Promise<WbSupplyCapacityReport> {
  const { data } = await api.get<WbSupplyCapacityReport>("/supply/analytics/ozon/supply-capacity");
  return data;
}

// ── Яндекс.Маркет (та же форма отчёта, регион = склад YM) ────────────────────
export async function getYmAnalytics(params?: CalcParams): Promise<WbAnalyticsReport> {
  const { data } = await api.get<WbAnalyticsReport>("/supply/analytics/ym", { params });
  return data;
}

// Единый пул готово/доупак на товар (items ключуются по nom_key), общий WB+Ozon.
// `assemble: false` — только «Остаток ГП»: раскрытие спецификаций и ATB в 1С стоят дорого, а при
// скрытой доупаковке их результат всё равно выбрасывался.
export async function getProductCapacity(assemble = true): Promise<WbSupplyCapacityReport> {
  const { data } = await api.get<WbSupplyCapacityReport>("/supply/analytics/supply-capacity", {
    params: { assemble },
  });
  return data;
}

export async function refreshOzonClusters(): Promise<{ warehouses: number; clusters: number }> {
  const { data } = await api.post<{ warehouses: number; clusters: number }>(
    "/supply/analytics/ozon/refresh-clusters",
  );
  return data;
}

// ── Единый планировщик: решения менеджера (товар × канал × регион → qty) ─────
export interface SupplyDecision {
  nom_key: string;
  channel: string; // wb | ozon
  region: string;
  qty: number;
  manager: string | null;
}

export async function getDecisions(): Promise<SupplyDecision[]> {
  const { data } = await api.get<SupplyDecision[]>("/supply/decisions");
  return data;
}

/** Выключенная цель поставки: канал целиком (`region === ""`) или отдельный регион/кластер/склад. */
export interface DisabledTarget {
  mp_code: string;
  region: string;
  note: string | null;
}

export async function getDisabledTargets(): Promise<DisabledTarget[]> {
  const { data } = await api.get<DisabledTarget[]>("/supply/targets");
  return data;
}

export async function setTargetEnabled(body: {
  mp_code: string;
  region?: string;
  /** массовое «отметить/снять все»: применить `enabled` сразу ко всем перечисленным регионам */
  regions?: string[];
  enabled: boolean;
  note?: string;
}): Promise<DisabledTarget[]> {
  const { data } = await api.put<DisabledTarget[]>("/supply/targets", body);
  return data;
}

/** Удалить ОДНО сохранённое «Решение» — инверсия putDecision по тому же ключу. */
export async function deleteDecision(key: {
  nom_key: string;
  channel: string;
  region: string;
}): Promise<{ deleted: number }> {
  const { data } = await api.delete<{ deleted: number }>("/supply/decision", { params: key });
  return data;
}

/** Удалить ВСЕ сохранённые «Решения» (у ручки намеренно нет параметров — см. роутер). */
export async function clearDecisions(): Promise<{ deleted: number }> {
  const { data } = await api.delete<{ deleted: number }>("/supply/decisions");
  return data;
}

export async function putDecision(body: {
  nom_key: string;
  channel: string;
  region: string;
  qty: number;
}): Promise<SupplyDecision> {
  const { data } = await api.put<SupplyDecision>("/supply/decision", body);
  return data;
}

// ── Фаза 4: движок распределения дефицитного пула (рекомендация) ────────────
export type AllocStrategy = "protect_then_margin" | "pure_margin";

export interface AllocationLine {
  channel: string;
  region: string;
  nom_key: string;
  article: string;
  need: number;
  allocated: number; // решение движка
  priority: number; // прибыль на очко упаковки
  reason: string; // full|deficit_pool|deficit_packaging|no_need|no_margin_residual
  trace: Record<string, number | string | boolean | null>; // числа/строки/флаги для «почему?»
  shares_flacon_with: string[]; // товары, делящие общий флакон (+ сколько им дал движок)
}

export interface AllocationReport {
  margin_as_of: string | null;
  candidates: number;
  margin_missing: number; // без маржи (YM/пробел)
  delisted: number; // «на вывод» исключено
  unbridged?: number; // sku с живым спросом без 1С-моста — исключены
  lines: AllocationLine[];
  // спрос WB: источник, дата остатка и предупреждения отчёта WB, по которым считал движок
  wb_source: WbSource | null;
  wb_as_of: string | null;
  // WB mart: допуск «данные за вчера» (сегодняшняя сборка dwh не прошла) и время сборки
  wb_mart_stale: boolean;
  wb_mart_built_at: string | null;
  wb_warnings: string[];
}

export interface AllocParams extends CalcParams {
  strategy?: AllocStrategy;
  floor_days?: number;
  packaging_per_day?: number;
  packaging_horizon?: number;
  set_points?: number;
  /** товары «сначала эти»: забирают дефицитный пул первыми (остальные считаются из остатка) */
  priority_nom?: string[];
  /** делить ТОЛЬКО остаток ГП: без доупака и мощности упаковки (флаконная модель выключена) */
  ready_only?: boolean;
  /** считать только эти каналы (пусто — все); пул при этом полный */
  channel?: string[];
  /** считать только эти регионы (пусто — все) */
  region?: string[];
}

/** Каким юрлицом поедет позиция — тем же каскадом, что и фиксация.
 *  `legal_name` пусто = не нашли ни в остатках, ни в заказах DWH: такую позицию 1С-заказ
 *  заблокирует, и прятать её за фильтром нельзя. */
export interface LegalRow {
  channel: string;
  nom_key: string;
  mp_sku: string;
  legal_name: string;
  legal_source: string;
}

/** Мост «канал + номенклатура → sku» с экрана: он уже есть в отчётах спроса. */
export interface LegalPair {
  channel: string;
  nom_key: string;
  mp_sku: string;
}

/** Карточка канала из 1С (`GET /supply/channel-skus`): мост + имя/артикул номенклатуры и квант со
 *  спеки канала. Имя/артикул/квант нужны товару без движения ни на одной площадке (новинка): его
 *  нет ни в одном отчёте спроса, и экран сеет его отсюда целиком. `in_box` null — в спеке не задан. */
export interface ChannelCard extends LegalPair {
  name: string | null;
  article: string | null;
  in_box: number | null;
}

/** ⚠️ POST, потому что мост — это сотни пар, и в query-строку он не влезает. Ходить за ним в 1С
 *  на сервере стоило 31 секунду из 59 на каждом открытии экрана. */
/** Мост «канал + номенклатура → sku» по ВСЕМ продаваемым позициям — включая те, по которым на
 *  маркетплейсе ещё не было ни одного движения. Отчёты спроса такие карточки не показывают вовсе
 *  (они строятся по движению), и без этого моста завезти товар «куда ещё ни разу не возили» было
 *  нечем: строки просто не существовало. На сервере кэшируется, ≈30 с только на холодную. */
export async function getChannelSkus(): Promise<ChannelCard[]> {
  const { data } = await api.get<ChannelCard[]>("/supply/channel-skus");
  return data;
}

export async function getLegalsMap(pairs: LegalPair[]): Promise<LegalRow[]> {
  const { data } = await api.post<LegalRow[]>("/supply/legals-map", { pairs });
  return data;
}

export async function getAllocation(params?: AllocParams): Promise<AllocationReport> {
  // priority_nom — повторяющийся query-параметр (?priority_nom=a&priority_nom=b), как ждёт FastAPI
  const { data } = await api.get<AllocationReport>("/supply/analytics/allocation", {
    params,
    paramsSerializer: { indexes: null },
  });
  return data;
}

// ── Производство: спрос → что произвести (ГП + флаконы) + заказы 1С с датами ──
export interface ProductionOrder {
  number: string;
  date: string | null;
  state: string; // work | plan
  bucket: string; // work_2w | work_later | plan
  qty: number;
  unit: string;
}
export interface ProductComponent {
  name: string;
  char: string;
  kind: string; // flacon | pack | other
  norm: number;
  needed: number;
  avail: number;
}
export interface ProductionProduct {
  nom_key: string;
  article: string;
  name: string;
  category: string;
  margin: number | null;
  need: number;
  by_channel: Record<string, number>;
  ready: number;
  doupak: number;
  deficit: number;
  in_work_2w: number;
  in_work_later: number;
  in_plan: number;
  unit: string;
  net: number;
  coverage_days: number | null;
  margin_at_risk: number;
  tier: number; // 0 горит 1 срочно 2 плановый 3 покрыт 4 убыточный (ярлык/цвет — карта TIER на фронте)
  assembly_block?: string; // "" | "no_base" | "spec_problem"
  orders: ProductionOrder[];
  components: ProductComponent[];
}
export interface ProductionComponentGlobal {
  name: string;
  char: string;
  category: string;
  kind: string;
  needed: number;
  avail: number;
  produce: number;
  in_work_2w: number;
  in_work_later: number;
  in_plan: number;
  unit: string;
  net: number;
  consumers: { article: string; name?: string; qty: number }[];
  orders?: ProductionOrder[];
}
export interface ProductionCategory {
  category: string;
  positions: number;
  burning: number;
  net: number;
  margin_at_risk: number;
  min_coverage: number | null;
  flag: string; // burn | soon | ok
}
export interface ProductionReport {
  generated_at: string | null;
  margin_as_of: string | null;
  window_start: string;
  window_end: string;
  orders_total: number;
  orders_dropped: number;
  products: ProductionProduct[];
  components: ProductionComponentGlobal[];
  categories: ProductionCategory[];
  totals: Record<string, number>;
  warnings: string[];
  // спрос WB: источник, дата остатка и предупреждения отчёта WB
  wb_source: WbSource | null;
  wb_as_of: string | null;
  // WB mart: допуск «данные за вчера» (сегодняшняя сборка dwh не прошла) и время сборки
  wb_mart_stale: boolean;
  wb_mart_built_at: string | null;
  wb_warnings: string[];
}

export async function getProductionNeeds(params?: CalcParams): Promise<ProductionReport> {
  const { data } = await api.get<ProductionReport>("/supply/analytics/production-needs", { params });
  return data;
}

export interface RegionMapRow {
  id: number;
  mp_code: string;
  source_type: string; // okrug | warehouse
  source_value: string;
  region: string;
}

export async function createRegionMap(body: {
  mp_code: string;
  source_type: string;
  source_value: string;
  region: string;
}): Promise<RegionMapRow> {
  const { data } = await api.post<RegionMapRow>("/supply/region-map", body);
  return data;
}

// ── Слой исполнения: Поставки (Фаза 5) ──
export interface ShipmentLine {
  id: number;
  nom_key: string;
  article: string;
  mp_sku: string;
  barcode: string | null;
  nom_name: string;
  qty: number;
  /** чем определилось юрлицо: stock | orders | stock_history (+"_ambiguous"), "" = не определилось */
  legal_source?: string;
}
export interface Shipment {
  id: number;
  mp_code: string;
  legal_name: string;
  region: string;
  warehouse: string | null;
  mp_supply_number: string | null;
  state: string;
  filename: string;
  onec_order_ref: string | null;
  onec_order_number: string | null;
  lines: ShipmentLine[];
}
export interface ShipmentRun {
  id: number;
  created_by: string;
  created_at: string;
  note: string | null;
  reserve_status: string;
  reserve_note: string | null;
  shipments: Shipment[];
}
export interface ShipmentRunListItem {
  id: number;
  created_by: string;
  created_at: string;
  note: string | null;
  reserve_status: string;
  shipments_count: number;
  /** поставок не-Ozon в фиксации: пока их нет нигде, вкладка «Отгрузки» скрыта */
  non_ozon_count: number;
}

export interface FixLine {
  nom_key: string;
  channel: string;
  region: string;
  qty: number;
}
export async function fixDistribution(lines: FixLine[], note?: string): Promise<ShipmentRun> {
  const { data } = await api.post<ShipmentRun>("/supply/shipments/fix", { lines, note: note ?? null });
  return data;
}
export async function getShipmentRuns(): Promise<ShipmentRunListItem[]> {
  const { data } = await api.get<ShipmentRunListItem[]>("/supply/shipments/runs");
  return data;
}
export async function getShipmentRun(id: number): Promise<ShipmentRun> {
  const { data } = await api.get<ShipmentRun>(`/supply/shipments/runs/${id}`);
  return data;
}
export async function patchShipment(
  id: number,
  body: {
    warehouse?: string;
    mp_supply_number?: string;
    state?: string;
    line_qty?: Record<number, number>;
    legal_name?: string;
  },
): Promise<Shipment> {
  const { data } = await api.patch<Shipment>(`/supply/shipments/${id}`, body);
  return data;
}
export async function getLegals(): Promise<string[]> {
  const { data } = await api.get<string[]>("/supply/legals");
  return data;
}
export async function deleteShipment(id: number): Promise<void> {
  await api.delete(`/supply/shipments/${id}`);
}
export async function deleteShipmentRun(id: number): Promise<void> {
  await api.delete(`/supply/shipments/runs/${id}`);
}
// создать «Заказ покупателя» в 1С из поставки (идемпотентно) + прикрепить файл
export async function createShipment1cOrder(id: number): Promise<Shipment> {
  const { data } = await api.post<Shipment>(`/supply/shipments/${id}/create-1c-order`);
  return data;
}
// скачать файл поставки (blob + триггер загрузки под именем filename)
export async function downloadShipmentFile(shipment: Shipment): Promise<void> {
  const { data } = await api.get(`/supply/shipments/${shipment.id}/file`, { responseType: "blob" });
  saveBlob(data as Blob, shipment.filename);
}

// ── Пополнение ФБС: конфиг складов, настройки, отчёт ─────────────────────────
/** Канал площадки, чей спрос FBS закрывает склад: (площадка, юрлицо) и — не обязательно —
 *  склад продавца НА площадке. Пусто = весь спрос FBS юрлица (так заведена «Москва»). */
export interface FbsChannel {
  mp_code: "wb" | "ozon";
  legal_name: string;
  mp_warehouse_id?: number | null;
  mp_warehouse_name?: string | null;
}

/** Откуда «Остаток ФБС» склада: `wh_helper` — пул комнаты, тот же, что синк wh-helper публикует
 *  в WB (Москва); `onec` — свободный 1С организации склада (региональные). */
export type FbsStockSource = "onec" | "wh_helper";

export interface FbsWarehouse {
  id: number;
  name: string;
  target_wh_ref: string; // склад-получатель в 1С (комната ФБС)
  target_wh_name: string;
  source_wh_ref: string; // склад-источник (Склад МП)
  source_wh_name: string;
  org_key: string; // организация-владелец товара
  channels: FbsChannel[];
  stock_source: FbsStockSource;
  active: boolean;
}

/** Тело создания склада ФБС = вся карточка без `id`. */
export type FbsWarehouseBody = Omit<FbsWarehouse, "id">;

export async function getFbsWarehouses(): Promise<FbsWarehouse[]> {
  const { data } = await api.get<FbsWarehouse[]>("/supply/fbs/warehouses");
  return data;
}

export async function createFbsWarehouse(body: FbsWarehouseBody): Promise<FbsWarehouse> {
  const { data } = await api.post<FbsWarehouse>("/supply/fbs/warehouses", body);
  return data;
}

/** Частичное обновление: передаём ТОЛЬКО правленые поля (бэк отвергает явный `null`). */
export async function updateFbsWarehouse(
  id: number,
  body: Partial<FbsWarehouseBody>,
): Promise<FbsWarehouse> {
  const { data } = await api.put<FbsWarehouse>(`/supply/fbs/warehouses/${id}`, body);
  return data;
}

export async function deleteFbsWarehouse(id: number): Promise<void> {
  await api.delete(`/supply/fbs/warehouses/${id}`);
}

// ── Справочники для пикеров формы «Склады ФБС» (живые 1С / Infisical / WB) ───
/** Элемент справочника 1С: ссылка (GUID) + имя. */
export interface OneCRef {
  ref_key: string;
  name: string;
}

/** Ответ поиска складов 1С: список + признак усечения (в 1С складов с таким именем больше,
 *  чем пришло — список неполный, а не «это все склады»). */
export interface OneCWarehouseSearch {
  items: OneCRef[];
  truncated: boolean;
}

/** Склады 1С (тип «Склад»); `q` пустой — весь список. Ошибку 1С не глотаем — пусть всплывёт. */
export async function getFbsOnecWarehouses(q: string): Promise<OneCWarehouseSearch> {
  const { data } = await api.get<OneCWarehouseSearch>("/supply/fbs/refs/onec-warehouses", {
    params: q ? { q } : undefined,
  });
  return data;
}

export async function getFbsOrganizations(): Promise<OneCRef[]> {
  const { data } = await api.get<OneCRef[]>("/supply/fbs/refs/organizations");
  return data;
}

/** Юрлица кабинетов площадки (имена; токены остаются на бэке). */
export async function getFbsLegals(mp: FbsChannel["mp_code"]): Promise<string[]> {
  const { data } = await api.get<{ legal_name: string }[]>("/supply/fbs/refs/legals", {
    params: { mp },
  });
  return data.map((r) => r.legal_name);
}

/** Склад продавца НА площадке (пока только WB). */
export interface MpWarehouse {
  id: number;
  name: string;
}

export async function getFbsMpWarehouses(
  mp: FbsChannel["mp_code"],
  legal: string,
): Promise<MpWarehouse[]> {
  const { data } = await api.get<MpWarehouse[]>("/supply/fbs/refs/mp-warehouses", {
    params: { mp, legal },
  });
  return data;
}

/** Настройки расчёта ФБС, сохранённые ЗА ПОЛЬЗОВАТЕЛЕМ. Дефолты живут на бэке — фронт их не
 *  дублирует, а ждёт этот ответ перед первым расчётом (как на «Распределении»). */
export interface FbsParams {
  horizon_days: number; // на сколько дней продаж пополняем
  window_days: number; // окно истории заказов FBS
  stale_days: number; // старше — заказ на перемещение считается застрявшим
  round_to_box: boolean;
  saved: boolean; // false = своих настроек нет, показаны дефолты
}

export async function getFbsParams(): Promise<FbsParams> {
  const { data } = await api.get<FbsParams>("/supply/fbs/params");
  return data;
}

export async function saveFbsParams(body: Omit<FbsParams, "saved">): Promise<FbsParams> {
  const { data } = await api.put<FbsParams>("/supply/fbs/params", body);
  return data;
}

/** Сброс к дефолтам кода = удалить свою строку, а не записать сегодняшние числа. */
export async function resetFbsParams(): Promise<FbsParams> {
  const { data } = await api.delete<FbsParams>("/supply/fbs/params");
  return data;
}

/** Слагаемые расчёта строки для «Почему?». Значения разнородные (числа, даты, список SKU),
 *  поэтому тип широкий, а читают его типизированные хелперы страницы. */
export type FbsTrace = Record<string, number | string | boolean | string[] | null>;

export interface FbsReportRow {
  nom_key: string;
  nom_name: string;
  article: string;
  mp_skus: string[]; // «wb:123456»
  orders_qty: number; // заказов FBS за окно
  speed: number; // шт/день
  stock_qty: number; // остаток на складе ФБС
  in_transit_qty: number; // едет заказами на перемещение (свежими)
  depth_days: number | null; // null = продаж за окно нет, глубина не определена
  need: number;
  recommended: number;
  source_free: number; // свободно на складе-источнике
  in_box: number; // штук в коробе (0 = не задано в 1С)
  flags: string[];
  trace: FbsTrace;
  stock_detail: FbsStockDetail;
  in_transit_detail: FbsTransitDoc[]; // Σ qty = in_transit_qty
}

/** Заказ на перемещение в «в пути» строки: сколько штук этой номенклатуры он везёт. */
export interface FbsTransitDoc {
  number: string;
  date: string; // YYYY-MM-DD
  state: string; // словами: «В работе» / «К отгрузке»
  qty: number;
}

/** Лежит в комнате или едет туда, но в карточке 1С не заполнен SKU WB (`not_in_meta`): в расчёт
 *  WB не входит, показывается отдельным блоком без ввода количества. Имя/артикул — из 1С. */
export interface FbsNoWbCardRow {
  nom_key: string;
  nom_name: string;
  article: string;
  orders_qty: number;
  stock_qty: number;
  stock_detail: FbsStockDetail;
  in_transit_qty: number;
  in_transit_detail: FbsTransitDoc[];
}

/** Свободный 1С одного юрлица в «Остатке ФБС». */
export interface FbsStockOrg {
  ref: string;
  name: string | null; // wh-helper знает имя не всех юрлиц; `onec` — из 1С (null — не нашлось)
  qty: number;
}

/** Разбивка «Остатка ФБС» строки. `onec`: одно юрлицо склада, остальное 0. `wh_helper`:
 *  `stock_qty = Σ by_org − in_flight − wb_unassembled − ozon_unassembled` (без клампа, может быть < 0). */
export interface FbsStockDetail {
  source: FbsStockSource;
  by_org: FbsStockOrg[];
  in_flight: number; // собрано порталом склада, но не проведено в 1С
  wb_unassembled: number; // несобранные задания WB
  ozon_unassembled: number; // несобранные задания Ozon
}

/** Товар без продаж ФБС: свободный ГП > 0 и SKU WB в 1С, а строки в отчёте нет. Рекомендации
 *  нет — количество вводит человек (уходит в тот же заказ на перемещение). */
export interface FbsNoSalesRow {
  nom_key: string;
  nom_name: string;
  article: string;
  mp_skus: string[]; // «wb:123456»
  stock_qty: number; // остаток ФБС по источнику склада (обычно 0)
  source_free: number;
  in_box: number;
}

/** Застрявший заказ на перемещение: в «в пути» не учтён, но показан человеку. */
export interface FbsStaleOrder {
  number: string;
  date: string;
  qty: number;
}

/** SKU площадки, которому не нашлась номенклатура 1С — его спрос в расчёт не попал. */
export interface FbsUnmappedSku {
  mp_code: string;
  mp_sku: string;
}

/** Сверка двух источников спроса ФБС по юрлицу за окно — справочно, в шапку отчёта.
 *  `by_warehouse` — задания `wb_fbs_orders_api` по складам продавца, `all_mp` — заказы
 *  `all_mp_orders` с признаком fbs. Источники разные, сходиться в ноль не обязаны. */
export interface FbsRecon {
  legal_name: string;
  by_warehouse: number;
  all_mp: number;
  diff_pct: number | null; // null = в all_mp_orders ноль, процент не определён
}

export interface FbsReport {
  as_of: string;
  orders_from: string;
  orders_to: string;
  warehouse: FbsWarehouse;
  params: FbsParams;
  stock_source: FbsStockSource;
  stock_as_of: string; // ISO datetime: на когда «Остаток ФБС» (wh_helper — расчёт пула)
  stock_last_sync_at: string | null; // последний удачный синк wh-helper; onec — null
  rows: FbsReportRow[];
  no_sales_rows: FbsNoSalesRow[];
  no_wb_card_rows: FbsNoWbCardRow[]; // nom не пересекается с rows и no_sales_rows
  stale_transfer_orders: FbsStaleOrder[];
  unmapped_skus: FbsUnmappedSku[];
  warnings: string[];
  stock_sync_notes: string[]; // замечания прогона синка wh-helper (не предупреждения); onec — []
  reconciliation: FbsRecon[];
}

export interface FbsReportQuery {
  warehouse_id: number;
  horizon_days: number;
  window_days: number;
  stale_days: number;
  round_to_box: boolean;
}

export async function getFbsReport(query: FbsReportQuery): Promise<FbsReport> {
  const { data } = await api.get<FbsReport>("/supply/fbs/report", { params: query });
  return data;
}

// ── Пополнение ФБС: заказ на перемещение в 1С ────────────────────────────────
/** Строка будущего документа. Количество — ЦЕЛОЕ число штук > 0 (бэк отвергает остальное). */
export interface FbsTransferLine {
  nom_key: string;
  qty: number;
}

export interface FbsTransferOrderRequest {
  warehouse_id: number;
  horizon_days: number; // уезжает в комментарий документа 1С
  lines: FbsTransferLine[];
}

/** Ответ создания: документ УЖЕ в 1С и проведён. */
export interface FbsTransferOrderResult {
  doc_ref: string;
  doc_number: string;
  doc_date: string;
  lines_count: number;
  qty_total: number;
}

/** Строка, которой не хватило на складе-источнике (тело отказа 409). */
export interface FbsTransferOverLine {
  nom_key: string;
  article: string;
  nom_name: string;
  qty: number;
  source_free: number;
}

/** Созданный порталом заказ — строка списка «Созданные заказы». */
export interface FbsTransferOrder {
  id: number;
  warehouse_id: number;
  doc_ref: string;
  doc_number: string;
  doc_date: string;
  horizon_days: number;
  lines_count: number;
  qty_total: number;
  user_id: number | null;
  user_name: string | null;
  created_at: string;
}

/** ⚠️ Повтор при ошибке ЗАПРЕЩЁН: 500 означает, что документ в 1С уже создан (номер — в тексте). */
export async function createFbsTransferOrder(
  body: FbsTransferOrderRequest,
): Promise<FbsTransferOrderResult> {
  const { data } = await api.post<FbsTransferOrderResult>("/supply/fbs/transfer-orders", body);
  return data;
}

export async function getFbsTransferOrders(warehouseId: number): Promise<FbsTransferOrder[]> {
  const { data } = await api.get<FbsTransferOrder[]>("/supply/fbs/transfer-orders", {
    params: { warehouse_id: warehouseId },
  });
  return data;
}
